import React from 'react';
import {useLocation, useMatch} from 'react-router-dom';
import {PATHS} from 'routes/constants';
import {APP_FLOWS, APP_PAGES} from 'utils/pages';
import {usePath} from './common';
import {useSummary} from 'context/summary';
import {useSchema} from 'context/guest';

function useNavigationCustom() {
  const location = useLocation();
  const {getMainPath} = usePath();
  const {isGuestRegistrationOnly} = useSummary();
  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();

  const rootPath = '/:token';

  const isHomeView = useMatch(rootPath);
  const isGuestRegistrationView = useMatch(getMainPath(PATHS.guestRegistration));
  const isVerificationView = useMatch(`${rootPath}/verification`);
  const isIdentityVerificationView = location.pathname.includes(PATHS.verification.main);
  const isTaxesView = useMatch(`${rootPath}/taxes`);
  const isPaymentsFormsViews = useMatch(`${rootPath}/payments/form`);
  const isPaymentsView =
    location.pathname.includes(PATHS.payments.main) && !isPaymentsFormsViews;
  const isDepositView = useMatch(getMainPath(PATHS.deposits));
  const isRemoteAccessView = useMatch(getMainPath(PATHS.remoteAccess));
  const isAddGuestFormView = useMatch(getMainPath(`${PATHS.addPersonalDataForm}/:id?`));
  const isCheckoutView = useMatch(getMainPath(PATHS.checkout.main));

  const isFAQFormView = useMatch(getMainPath(PATHS.faq));

  const currentPage = React.useMemo(() => {
    if (isGuestRegistrationView) {
      return APP_PAGES.registerGuest;
    }
    if (isVerificationView) {
      return APP_PAGES.verification;
    }
    if (isIdentityVerificationView) {
      return APP_PAGES.verificationChild;
    }
    if (isTaxesView) {
      return location.state?.from
        ? `${APP_PAGES.taxes}_${location.state?.from}`
        : APP_PAGES.taxes;
    }
    if (isPaymentsFormsViews) {
      return location.state?.from
        ? `${APP_PAGES.paymentsForm}_${location.state?.from}`
        : APP_PAGES.paymentsForm;
    }
    if (isPaymentsView) {
      return location.state?.from
        ? `${APP_PAGES.payments}_${location.state?.from}`
        : APP_PAGES.payments;
    }
    if (isDepositView) {
      return APP_PAGES.deposits;
    }
    if (isAddGuestFormView) {
      return APP_PAGES.addPersonalData;
    }
    if (isHomeView) {
      return APP_PAGES.home;
    }
    if (isRemoteAccessView) {
      return APP_PAGES.remoteAccess;
    }
    if (isFAQFormView) {
      return APP_PAGES.faq;
    }
    if (isCheckoutView) {
      return APP_PAGES.checkout;
    }
    return APP_PAGES.home;
  }, [
    isGuestRegistrationView,
    isVerificationView,
    isIdentityVerificationView,
    isTaxesView,
    isPaymentsFormsViews,
    isPaymentsView,
    isDepositView,
    isAddGuestFormView,
    isHomeView,
    isRemoteAccessView,
    isFAQFormView,
    isCheckoutView,
    location.state?.from,
  ]);

  const currentFlow = React.useMemo(() => {
    if (!isGuestRegistrationOnly) {
      return APP_FLOWS.allFeatures;
    }

    return ivEnabled ? APP_FLOWS.grIvOnly : APP_FLOWS.grOnly;
  }, [isGuestRegistrationOnly, ivEnabled]);

  const currentFlowAndPage = React.useMemo(() => {
    return `${currentFlow}_${currentPage}`;
  }, [currentFlow, currentPage]);

  return {currentPage, currentFlowAndPage, isPaymentsView, currentFlow};
}

export {useNavigationCustom};
